import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Card,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import {
  fetchNewsCategories,
  deleteNewsCategory,
  createNewsCategory,
  updateNewsCategory,
  NewsCategory,
} from "../../services/api";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
import { useSnackbar } from "notistack";

const NewsCategoryList: React.FC = () => {
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState<Omit<NewsCategory, "id">>({
    title: "",
    description: "",
    active: true,
    displayOrder: 0,
  });
  const [editCategory, setEditCategory] = useState<NewsCategory | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const {
    data: categories,
    isLoading,
    error,
    refetch,
  } = useQuery<NewsCategory[]>({
    queryKey: ["newsCategories"],
    queryFn: fetchNewsCategories,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNewsCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsCategories"] });
      enqueueSnackbar("Category deleted successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data || "Failed to delete category", {
        variant: "error",
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: createNewsCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsCategories"] });
      setShowAddForm(false);
      setNewCategory({
        title: "",
        description: "",
        active: true,
        displayOrder: 0,
      });
      enqueueSnackbar("Category created successfully", {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Failed to create category", { variant: "error" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateNewsCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsCategories"] });
      setEditingId(null);
      setEditCategory(null);
      enqueueSnackbar("Category updated successfully", {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Failed to update category", { variant: "error" });
    },
  });

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  const handleEditClick = (category: NewsCategory) => {
    setEditingId(category.id);
    setEditCategory({ ...category });
  };

  const handleSaveEdit = () => {
    if (editCategory) {
      updateMutation.mutate(editCategory);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditCategory(null);
  };

  const handleCreateCategory = () => {
    createMutation.mutate(newCategory);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">
          Error loading categories. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="News Categories"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "News", path: "/" },
          { label: "Categories" },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(true)}
          >
            Add Category
          </Button>
        }
      />

      {showAddForm && (
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Add New Category
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Title"
              value={newCategory.title}
              onChange={(e) =>
                setNewCategory((prev) => ({ ...prev, title: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Description"
              value={newCategory.description}
              onChange={(e) =>
                setNewCategory((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              size="small"
              sx={{ minWidth: 250 }}
            />
            <TextField
              label="Display Order"
              type="number"
              value={newCategory.displayOrder}
              onChange={(e) =>
                setNewCategory((prev) => ({
                  ...prev,
                  displayOrder: Number(e.target.value),
                }))
              }
              size="small"
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newCategory.active}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleCreateCategory}
              disabled={!newCategory.title.trim() || createMutation.isPending}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </Button>
          </Box>
        </Card>
      )}

      {categories && categories.length === 0 ? (
        <EmptyState
          title="No Categories Found"
          message="No news categories have been created yet. Add your first category to get started."
          icon={<CategoryIcon />}
          action={{
            label: "Add Category",
            onClick: () => setShowAddForm(true),
          }}
        />
      ) : (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell width="120">Display Order</TableCell>
                  <TableCell width="120">Status</TableCell>
                  <TableCell width="200">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories?.map((category: NewsCategory) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      {editingId === category.id ? (
                        <TextField
                          value={editCategory?.title || ""}
                          onChange={(e) =>
                            setEditCategory((prev) =>
                              prev ? { ...prev, title: e.target.value } : null
                            )
                          }
                          size="small"
                          fullWidth
                        />
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CategoryIcon
                            fontSize="small"
                            sx={{
                              color: theme.palette.primary.main,
                              opacity: 0.7,
                            }}
                          />
                          <Typography fontWeight="medium">
                            {category.title}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === category.id ? (
                        <TextField
                          value={editCategory?.description || ""}
                          onChange={(e) =>
                            setEditCategory((prev) =>
                              prev
                                ? { ...prev, description: e.target.value }
                                : null
                            )
                          }
                          size="small"
                          fullWidth
                          multiline
                        />
                      ) : (
                        category.description || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === category.id ? (
                        <TextField
                          type="number"
                          value={editCategory?.displayOrder || 0}
                          onChange={(e) =>
                            setEditCategory((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    displayOrder: Number(e.target.value),
                                  }
                                : null
                            )
                          }
                          size="small"
                        />
                      ) : (
                        category.displayOrder
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === category.id ? (
                        <Switch
                          checked={editCategory?.active || false}
                          onChange={(e) =>
                            setEditCategory((prev) =>
                              prev
                                ? { ...prev, active: e.target.checked }
                                : null
                            )
                          }
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={category.active ? "Active" : "Inactive"}
                          color={category.active ? "success" : "default"}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            minWidth: 80,
                            textAlign: "center",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {editingId === category.id ? (
                          <>
                            <Tooltip title="Save Changes">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleSaveEdit}
                                disabled={updateMutation.isPending}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit Category">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditClick(category)}
                                sx={{ boxShadow: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Category">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(category.id)}
                                sx={{ boxShadow: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={deleteId !== null}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this category? This action cannot be
            undone. All news articles in this category will become
            uncategorized.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewsCategoryList;
